/**
 * The inclusive day-count seam for bookings. SiFly bills by the day like a rental
 * car: every calendar day the gear is held is charged, INCLUDING both the pickup
 * day and the drop-off day. The calendar speaks the inclusive drop-off date the
 * customer tapped; the DB stores half-open `[start, end)` intervals and overlaps
 * on them. These two pure helpers bridge the two so the proven overlap SQL never
 * has to change:
 *
 *   bookingDays(pickup, dropoff) → inclusive day count for pricing/display
 *   toExclusiveEnd(dropoff)      → dropoff + 1 = the exclusive checkout boundary
 *
 * Storing `[pickup, dropoff + 1)` occupies pickup..dropoff inclusive (so the
 * drop-off day is blocked) and makes `end_date − start_date` equal the inclusive
 * day count (so the drop-off day is charged). Pure + dependency-free; dates are
 * 'YYYY-MM-DD' compared/advanced in UTC to match the booking-math contract.
 */

const DAY_MS = 86_400_000

/** Inclusive days between a pickup and drop-off date: `(dropoff − pickup) + 1`. */
export function bookingDays(pickup: string, dropoff: string): number {
  const ms = Date.parse(dropoff + 'T00:00:00Z') - Date.parse(pickup + 'T00:00:00Z')
  return Math.round(ms / DAY_MS) + 1
}

/** The inclusive drop-off date advanced one day to the exclusive checkout boundary. */
export function toExclusiveEnd(dropoff: string): string {
  const d = new Date(Date.parse(dropoff + 'T00:00:00Z') + DAY_MS)
  return d.toISOString().slice(0, 10)
}

/**
 * Inverse of `toExclusiveEnd`: the stored exclusive `end_date` back to the
 * customer-facing inclusive drop-off day (`end_date − 1`). Use this anywhere a
 * stored booking's end is shown to a human — the raw `end_date` is one day past
 * the day they actually return.
 */
export function toInclusiveDropoff(exclusiveEnd: string): string {
  const d = new Date(Date.parse(exclusiveEnd + 'T00:00:00Z') - DAY_MS)
  return d.toISOString().slice(0, 10)
}
